import { Capacitor } from "@capacitor/core";
import { useContext } from "react";
import AppContext from "../../context";

import styles from '../../styles/SidebarMembershipStatus.module.scss';
import {useEffect, useState} from "react";
import Data from "../../repository";
import moment from "moment";

function SidebarMembershipStatus() {
    const context = useContext(AppContext);

    const [loading, setLoading] = useState(true);
    const [memberships, setMemberships] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const isWorldpay = context.domain?.paymentProcessor === 'Worldpay';
    const isSumup = context.domain?.paymentProcessor === 'SumUp';

    useEffect(async () => {
        await loadMemberships();
    }, [])

    const loadMemberships = async () => {
        setLoading(true);
        if (isWorldpay || isSumup) {
            const result = isSumup
                ? await Data.fetchSumupSubscriptions()
                : await Data.fetchWorldpaySubscriptions();
            const membershipSubs = result.data.filter(sub => sub.type === 'membership');
            setMemberships(membershipSubs);
        } else {
            const result = await Data.fetchMemberships();
            setMemberships(result.data);
        }
        setLoading(false);
    }

    const handlePause = async (subscriptionId) => {
        setActionLoading(true);
        try {
            if (isSumup) {
                await Data.pauseSumupSubscription(subscriptionId);
            } else {
                await Data.pauseWorldpaySubscription(subscriptionId);
            }
            await loadMemberships();
        } catch (err) {
            alert('Failed to pause subscription. Please try again.');
        }
        setActionLoading(false);
    }

    const handleResume = async (subscriptionId) => {
        setActionLoading(true);
        try {
            if (isSumup) {
                await Data.resumeSumupSubscription(subscriptionId);
            } else {
                await Data.resumeWorldpaySubscription(subscriptionId);
            }
            await loadMemberships();
        } catch (err) {
            alert('Failed to resume subscription. Please try again.');
        }
        setActionLoading(false);
    }

    const handleCancel = async (subscriptionId) => {
        const confirmMsg = 'Are you sure you want to cancel your membership? Your access will continue until the end of your current billing period.';

        if (!window.confirm(confirmMsg)) return;

        setActionLoading(true);
        try {
            if (isWorldpay || isSumup) {
                if (isSumup) {
                    await Data.cancelSumupSubscription(subscriptionId, false);
                } else {
                    await Data.cancelWorldpaySubscription(subscriptionId, false);
                }
                await loadMemberships();
            } else {
                // For Stripe, redirect to billing portal
                const result = await Data.fetchBillingPortalSession('subscription_cancel');
                window.location.href = result.data.url;
            }
        } catch (err) {
            alert('Failed to cancel subscription. Please try again.');
            setActionLoading(false);
        }
    }

    const getStatusText = (membership) => {
        if (isWorldpay || isSumup) {
            if (membership.status === 'active' && membership.cancelAtPeriodEnd) return 'Cancelling';
            if (membership.status === 'active') return 'Active';
            if (membership.status === 'paused') return 'Paused';
            if (membership.status === 'past_due') return 'Past Due';
            if (membership.status === 'cancelled') return 'Cancelled';
            return 'Inactive';
        } else {
            if (membership.status === 'active') return 'Active';
            if (membership.status === 'past_due') return 'Past Due';
            if (membership.status === 'canceled') return 'Cancelled';
            return 'Inactive';
        }
    }

    if(loading) return null

    const currentMembership = memberships.length > 0 ? memberships[memberships.length - 1] : null;
    const hasActiveMembership = currentMembership &&
        ((isWorldpay || isSumup)
            ? ['active', 'paused', 'past_due'].includes(currentMembership.status)
            : ['active', 'past_due'].includes(currentMembership.status)
        );

    return (
        <div className={styles.sidebarMembershipStatus}>
            <div className={styles.statusHeader} onClick={() => hasActiveMembership && setExpanded(!expanded)}>
                <p>Membership <strong>{currentMembership ? getStatusText(currentMembership) : 'Inactive'}</strong></p>
                {hasActiveMembership && (
                    <span className={styles.expandIcon}>{expanded ? '−' : '+'}</span>
                )}
            </div>

            {expanded && hasActiveMembership && (
                <div className={styles.expandedContent}>
                    {(isWorldpay || isSumup) && currentMembership.MembershipPlan && (
                        <>
                            <div className={styles.planInfo}>
                                <p className={styles.planName}>{currentMembership.MembershipPlan.name}</p>
                                <p className={styles.planPrice}>
                                    £{(currentMembership.amount / 100).toFixed(2)} / {currentMembership.intervalCount} {currentMembership.interval}{currentMembership.intervalCount > 1 ? 's' : ''}
                                </p>
                                {currentMembership.nextBillingDate && currentMembership.status === 'active' && !currentMembership.cancelAtPeriodEnd && (
                                    <p className={styles.nextBilling}>
                                        Next billing: {moment(currentMembership.nextBillingDate).format('MMM D, YYYY')}
                                    </p>
                                )}
                            </div>

                            {currentMembership.status === 'active' && !currentMembership.cancelAtPeriodEnd && (
                                <div className={styles.actions}>
                                    <button
                                        className={styles.actionBtn}
                                        onClick={(e) => { e.stopPropagation(); handlePause(currentMembership._id); }}
                                        disabled={actionLoading}
                                    >
                                        Pause
                                    </button>
                                    <button
                                        className={`${styles.actionBtn} ${styles.danger}`}
                                        onClick={(e) => { e.stopPropagation(); handleCancel(currentMembership._id); }}
                                        disabled={actionLoading}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}

                            {currentMembership.status === 'paused' && (
                                <div className={styles.actions}>
                                    <button
                                        className={styles.actionBtn}
                                        onClick={(e) => { e.stopPropagation(); handleResume(currentMembership._id); }}
                                        disabled={actionLoading}
                                    >
                                        Resume
                                    </button>
                                </div>
                            )}

                            {currentMembership.cancelAtPeriodEnd && (
                                <p className={styles.cancelNote}>
                                    Ends {moment(currentMembership.currentPeriodEnd).format('MMM D, YYYY')}
                                </p>
                            )}
                        </>
                    )}

                    {!isWorldpay && currentMembership.plan && (
                        <>
                            <div className={styles.planInfo}>
                                <p className={styles.planName}>{currentMembership.plan.name}</p>
                                <p className={styles.planPrice}>
                                    £{(currentMembership.plan.amount / 100).toFixed(2)} / {currentMembership.plan.interval_count} {currentMembership.plan.interval}{currentMembership.plan.interval_count > 1 ? 's' : ''}
                                </p>
                                {currentMembership.current_period_end && (
                                    <p className={styles.nextBilling}>
                                        {currentMembership.cancel_at_period_end
                                            ? `Ends ${moment(currentMembership.current_period_end * 1000).format('MMM D, YYYY')}`
                                            : `Next billing: ${moment(currentMembership.current_period_end * 1000).format('MMM D, YYYY')}`
                                        }
                                    </p>
                                )}
                            </div>

                            <div className={styles.actions}>
                                <button
                                    className={styles.actionBtn}
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        setActionLoading(true);
                                        try {
                                            const result = await Data.fetchBillingPortalSession(undefined);
                                            window.location.href = result.data.url;
                                        } catch (err) {
                                            alert('Failed to open billing portal.');
                                            setActionLoading(false);
                                        }
                                    }}
                                    disabled={actionLoading}
                                >
                                    Manage
                                </button>
                                {currentMembership.status === 'active' && !currentMembership.cancel_at_period_end && (
                                    <button
                                        className={`${styles.actionBtn} ${styles.danger}`}
                                        onClick={(e) => { e.stopPropagation(); handleCancel(currentMembership.id); }}
                                        disabled={actionLoading}
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    )
}

export default SidebarMembershipStatus;
